/**
 * The record change feed: the three kinds of record an Account syncs (issue #25) announce every
 * save and every deletion here, so the sync engine hears about a change without `store.ts` or
 * `personal.ts` knowing that sync exists.
 *
 * Announcements are hints, never the truth: the engine decides what is dirty by comparing each
 * record's `updatedAt` against the one it last pushed or pulled, so a missed or a duplicated
 * announcement costs a sync pass and never a change.
 */
import type { SyncCollection } from "../../shared/sync-format";

export interface RecordChange {
  collection: SyncCollection;
  id: string;
  kind: "saved" | "deleted";
  /**
   * On a deletion, the record as it was. The sync engine keeps it as the local Recently Deleted
   * copy, which is the only moment it can be read; the Account keeps its own for thirty days.
   */
  before?: unknown;
}

type Listener = (change: RecordChange) => void;

const listeners = new Set<Listener>();

export function subscribeRecords(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Announce a change. A listener that throws never reaches the writer that saved. */
export function recordChanged(collection: SyncCollection, id: string, kind: RecordChange["kind"], before?: unknown): void {
  const change: RecordChange = before === undefined ? { collection, id, kind } : { collection, id, kind, before };
  for (const listener of [...listeners]) {
    try {
      listener(change);
    } catch (e) {
      console.warn("a record listener failed", e);
    }
  }
}
